import React, { useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useNavigate, useLocation } from 'react-router-dom';
import urlcat from 'urlcat';
import queryString from 'query-string';
import useHandleAuthErrors from 'utils/useHandleAuthErrors';
import useHandleLoginRedirect from 'utils/useHandleLoginRedirect';
import useHandleMfaEnrollment from 'utils/useHandleMfaEnrollment';
import useSearchParams from 'utils/useQueryParams';
import config from 'utils/config';
import { getIdpFromWebfinger } from 'utils/webfinger';
import SignInForm from 'pages/Login/SignInForm';
import {
  setUserNameCookie,
  clearUserNameCookie,
  getUserNameFromCookie,
} from 'utils/cookies';
import mapAuthnTransactionToMfaFactors from 'utils/mfaFactorHelpers';

const getIdpForUser = getIdpFromWebfinger(config.oneIdManagementApi);

export interface LoginValues {
  username: string;
  password: string;
  rememberme: boolean;
}

const SignIn: React.FC = () => {
  const { oktaAuth } = useOktaAuth();
  const navigate = useNavigate();
  const [{ pwd }] = useSearchParams();
  const [errorMessage, setErrorMessage] = useHandleAuthErrors();
  const [loading, setLoading] = useState(false);
  const [userNameState, setUserNameState] = useState(() => {
    return getUserNameFromCookie();
  });

  const location = useLocation();
  const { state } = location;

  const handleLoginRedirect = useHandleLoginRedirect();
  const handleMfaEnrollment = useHandleMfaEnrollment();

  useEffect(() => {
    if (state?.username) {
      setUserNameState(state.username);
    }
  }, [state]);

  const [showPasswordField, setShowPasswordField] = useState<boolean>(false);

  const handleIdPLookup = useCallback(
    async (username?: string) => {
      const idpInfo = await getIdpForUser(username);
      // Sign in with the IdP if there is only one and the user is not an admin
      if (
        idpInfo?.idpList.length === 1 &&
        !idpInfo?.isPasswordSignInAvailable
      ) {
        oktaAuth.signInWithRedirect({
          idp: idpInfo.idpList[0].id,
          loginHint: username,
        });
        return true;
      }

      if (idpInfo?.idpList.length > 0) {
        navigate(
          urlcat(`/login/sso/choose`, {
            ...queryString.parse(window.location.search),
          }),
          {
            state: {
              username,
              idpList: idpInfo.idpList,
              isPasswordSignInAvailable: idpInfo.isPasswordSignInAvailable,
            },
          },
        );
        return true;
      }
      return false;
    },
    [oktaAuth, navigate],
  );

  const handleChangeUserName = (nameFromForm: string) => {
    setUserNameState(nameFromForm);
    setUserNameCookie(nameFromForm);
  };

  const handleMfaChallenge = useCallback(
    async (result: any) => {
      const factors = mapAuthnTransactionToMfaFactors(result);

      const newState = {
        username: userNameState,
        factors,
      };
      navigate('/login/mfa/verify', {
        state: newState,
      });
    },
    [navigate, userNameState],
  );

  useEffect(() => {
    (async () => {
      // you really only want to show the password field here if the user is on /login?pwd
      setShowPasswordField(pwd !== undefined && !!userNameState);
    })();
  }, [pwd, userNameState]);

  const handleLogin = async ({
    username,
    password,
    rememberme,
  }: LoginValues) => {
    // only call webfinger if they're not entering their password
    if (username && !showPasswordField) {
      const idp = await handleIdPLookup(username);
      if (idp) {
        return;
      }
    }

    handleChangeUserName(username);

    // Handle 'Remember Me'
    if (!rememberme) {
      // expire cookie immediately
      clearUserNameCookie(username);
    }

    if (!showPasswordField) {
      // Display password field if no external IdP
      setErrorMessage(null);
      navigate(
        urlcat(`/login`, {
          pwd: '',
          ...queryString.parse(window.location.search),
        }),
      );
      return;
    }

    try {
      setLoading(true);
      // Get sessionToken with username and password
      const result = await oktaAuth.signInWithCredentials({
        username,
        password,
      });

      if (result.status === 'SUCCESS') {
        const { sessionToken } = result;
        await handleLoginRedirect(username, sessionToken);
      } else if (result.status === 'MFA_REQUIRED') {
        await handleMfaChallenge(result);
        return;
      } else if (result.status === 'MFA_ENROLL') {
        await handleMfaEnrollment(result, username);
        return;
      } else if (
        result.status === 'PASSWORD_EXPIRED' ||
        result.status === 'LOCKED_OUT'
      ) {
        // we don't support these as of now.
        // Maybe we can add support for these in the future.
        // or we can just show the error message with a more clear message
        throw new Error('User has expired password or is locked out.');
      }

      throw new Error('authentication result failed');
    } catch (error: any) {
      setErrorMessage(error, username);
      setLoading(false);
    }
  };

  return (
    <SignInForm
      formikConfig={{
        onSubmit: handleLogin,
        validate: (values) => {
          const errors: Record<string, string> = {};
          if (values.username?.length <= 0) {
            errors.username = 'Username is required';
          }
          if (showPasswordField && values.password?.length <= 0) {
            errors.password = 'Password is required';
          }
          return errors;
        },
        enableReinitialize: true,
        initialValues: {
          username: userNameState ?? getUserNameFromCookie(),
          password: '',
          rememberme: !!getUserNameFromCookie(),
        },
      }}
      errorMessage={errorMessage}
      loading={loading}
      setErrorMessage={setErrorMessage}
      showPasswordField={showPasswordField}
    />
  );
};
export default SignIn;
